import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Alert, CircularProgress, Divider,
} from '@mui/material';
import { Payments, Search, WhatsApp } from '@mui/icons-material';
import { adminGetWithdrawalByCode, adminGetWithdrawalDetail, adminSendPayoutOtp, adminPayCashWithdrawal } from 'api/withdrawal';
import { user } from 'localStorage';

const CURRENCY_SYMBOLS = { USD: '$', THB: '฿', LAK: '₭' };
const fmt = (amount, currency) =>
  `${CURRENCY_SYMBOLS[currency] || ''}${Number(amount || 0).toLocaleString('en-US', { maximumFractionDigits: currency === 'LAK' ? 0 : 2 })} ${currency}`;

const ID_TYPES = [
  { value: 'national_id', label: 'National ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'family_book', label: 'Family book' },
  { value: 'driver_license', label: 'Driving licence' },
  { value: 'other', label: 'Other' },
];

/**
 * Pay a customer's cash withdrawal at the counter. Opened either for a known
 * request (from the list) or empty, to look one up by the customer's 6-digit
 * pickup code. Steps: check ID vs account name → WhatsApp OTP the customer
 * reads out → pay from a drawer the staff member is assigned to.
 */
const CashPayoutDialog = ({ open, request: initialRequest, onClose, onPaid }) => {
  const [code, setCode] = useState('');
  const [req, setReq] = useState(null);
  // Drawers this staff member may pay from, with names (from the API); the
  // cached profile only has station ids, so it's just a fallback.
  const stations = req?.payStations || user()?.stations || [];
  const [stationId, setStationId] = useState('');
  const [idType, setIdType] = useState('');
  const [idLast4, setIdLast4] = useState('');
  const [nameMatches, setNameMatches] = useState(false);
  const [otpRef, setOtpRef] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const pick = (r) => {
    setReq(r);
    const list = r.payStations || user()?.stations || [];
    const mine = list.find((s) => s.id === r.stationId) || list.find((s) => s.default) || list[0];
    setStationId(mine?.id || '');
    setIdType(''); setIdLast4(''); setNameMatches(false); setOtpRef(''); setOtp(''); setError('');
  };

  useEffect(() => {
    if (!open) return;
    setCode(''); setError('');
    if (!initialRequest) { setReq(null); return; }
    pick(initialRequest);
    // Refresh from the detail endpoint for payStations + current wallet status.
    adminGetWithdrawalDetail(initialRequest._id).then((res) => pick(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialRequest]);

  const lookup = async () => {
    setBusy('lookup'); setError('');
    try {
      const res = await adminGetWithdrawalByCode(code);
      pick(res.data);
    } catch (e) {
      setError(e.message || 'Code not found');
    } finally { setBusy(''); }
  };

  const sendOtp = async () => {
    setBusy('otp'); setError('');
    try {
      const res = await adminSendPayoutOtp(req._id);
      setOtpRef(res.data.ref); setOtp('');
    } catch (e) {
      setError(e.message || 'Could not send the code');
    } finally { setBusy(''); }
  };

  const canPay = req && req.status === 'pending' && stationId && idType && /^[A-Za-z0-9]{4}$/.test(idLast4)
    && nameMatches && otpRef && otp.length === 6 && req.walletActive !== false;

  const pay = async () => {
    if (!canPay) return;
    if (!window.confirm(`Give ${fmt(req.sourceAmount, req.sourceCurrency)} in cash to ${req.customer?.name || 'the customer'}? This cannot be undone.`)) return;
    setBusy('pay'); setError('');
    try {
      await adminPayCashWithdrawal(req._id, { stationId, ref: otpRef, otp, idType, idLast4: idLast4.toUpperCase() });
      onPaid?.(`Paid ${fmt(req.sourceAmount, req.sourceCurrency)} to ${req.customer?.name || 'customer'}`);
      onClose();
    } catch (e) {
      setError(e.message || 'Payout failed');
    } finally { setBusy(''); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}><Payments color="success" sx={{ mr: 1 }} />Cash payout</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!req ? (
          <Box display="flex" gap={1} mt={1}>
            <TextField
              fullWidth size="small" label="Customer's 6-digit code" value={code}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) lookup(); }}
            />
            <Button variant="contained" color="secondary" startIcon={<Search />} disabled={code.length !== 6 || busy === 'lookup'} onClick={lookup}>Find</Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">Hand over in cash</Typography>
            <Typography variant="h4" fontWeight={800} color="secondary.main">{fmt(req.sourceAmount, req.sourceCurrency)}</Typography>
            <Typography variant="body1" fontWeight={700} mt={1}>{req.customer?.name || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">{req.customer?.whatsapp || req.customer?.phone}</Typography>
            <Typography variant="body2" color="text.secondary">Customer planned to collect at {req.station?.name || req.stationId || '—'}</Typography>
            {req.walletActive === false && <Alert severity="error" sx={{ mt: 1 }}>This wallet is inactive — reject the request instead of paying.</Alert>}
            {req.status !== 'pending' && <Alert severity="warning" sx={{ mt: 1 }}>This request is no longer pending.</Alert>}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="secondary">1 · Check ID</Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mt={1}>
              <FormControl size="small">
                <InputLabel>ID document</InputLabel>
                <Select value={idType} label="ID document" onChange={(e) => setIdType(e.target.value)}>
                  {ID_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                size="small" label="Last 4 of ID number" value={idLast4}
                inputProps={{ maxLength: 4 }}
                onChange={(e) => setIdLast4(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase())}
              />
            </Box>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox color="secondary" checked={nameMatches} onChange={(e) => setNameMatches(e.target.checked)} />}
              label={`Name on the ID matches "${req.customer?.name || ''}" and the photo matches the customer`}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="secondary">2 · WhatsApp code</Typography>
            <Box display="flex" gap={1} mt={1} alignItems="center">
              <Button variant="outlined" color="secondary" startIcon={<WhatsApp />} onClick={sendOtp} disabled={busy === 'otp' || req.status !== 'pending'}>
                {otpRef ? 'Send a new code' : "Send code to customer's WhatsApp"}
              </Button>
              {otpRef && (
                <TextField
                  size="small" label={`Code (ref ${otpRef})`} value={otp}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              )}
            </Box>

            <FormControl size="small" fullWidth sx={{ mt: 2 }}>
              <InputLabel>Pay from drawer</InputLabel>
              <Select value={stationId} label="Pay from drawer" onChange={(e) => setStationId(e.target.value)}>
                {stations.map((s) => <MenuItem key={s.id} value={s.id}>{s.name || s.id}</MenuItem>)}
              </Select>
            </FormControl>
            {stations.length === 0 && <Typography variant="caption" color="error">You have no assigned branch.</Typography>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">Cancel</Button>
        {req && (
          <Button variant="contained" color="success" onClick={pay} disabled={!canPay || busy === 'pay'} startIcon={busy === 'pay' ? <CircularProgress size={18} /> : <Payments />}>
            Pay out
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CashPayoutDialog;
